import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import {
  getAdditionalCostTypeFilteringOptions,
  getClearAdditionalCostTypeModel,
} from "../../common/EntitiesManagement";
import localize from "../../common/localize";
import ErrorMessage from "../../components/common/ErrorMessage";
import ModalBackground from "../../components/common/ModalBackground";
import AddOrEditAdditionalCostType from "./components/AddOrEditAdditionalCostType";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import PagePagination from "../../components/PagePagination";
import ActionButtons from "../../components/common/ActionButtons";
import TrueFalseImage from "../../components/TrueFalseImage";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import {
  generateNoDataValues,
  getFiltersInfo,
  getPaymentTypeModeLabel,
} from "../../common/utils";
import PaymentTypeValue from "./components/PaymentTypeValue";
import useGlobalContext from "../../hooks/useGlobalContext";
import EntityPrinter from "../../components/common/EntityPrinter";

export default function AdditionalCostTypes() {
  const {
    additionalCostTypeModel,
    setAdditionalCostTypeModel,
    additionalCostTypeData,
    ADDITIONAL_COST_TYPE_ENDPOINT,
    config,
    pageIndexAdditionalCostTypes,
    setPageIndexAdditionalCostTypes,
    toggleVisibleModalConfirm,
    deleteEntity,
    createEntity,
    updateEntity,
    setBasicFilterOptions,
    basicFilterData,
  } = useGlobalContext();
  const { id } = useParams();
  const [additionalCostTypeDataFiltered, setadditionalCostTypeDataFiltered] =
    useState(additionalCostTypeData.Data);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getAdditionalCostTypeFilteringOptions());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const additionalCostTypeDataObj = additionalCostTypeData.Data.find(
        (additionalCostTypeObj) => additionalCostTypeObj.Id === Number(id)
      );
      setAdditionalCostTypeModel(additionalCostTypeDataObj);
    }
  }, [id]);

  useEffect(() => {
    setadditionalCostTypeDataFiltered(additionalCostTypeData.Data);
  }, [additionalCostTypeData]);

  const validateOperationResult = (result, isDelete) => {
    setAdditionalCostTypeModel(getClearAdditionalCostTypeModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([ADDITIONAL_COST_TYPE_ENDPOINT, config]);

    if (isDelete) {
      if (
        pageIndexAdditionalCostTypes !== 0 &&
        additionalCostTypeData.Data.length === 1
      ) {
        setPageIndexAdditionalCostTypes(pageIndexAdditionalCostTypes - 1);
      }
    }

    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${ADDITIONAL_COST_TYPE_ENDPOINT}/${itemId}`}`,
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    const updateResult = await updateEntity(
      `${`${ADDITIONAL_COST_TYPE_ENDPOINT}/${data.Id}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const handleCreateRequest = async (data) => {
    const updateResult = await createEntity(
      `${`${ADDITIONAL_COST_TYPE_ENDPOINT}`}`,
      data,
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !additionalCostTypeModel.Name ||
    additionalCostTypeModel.Name.length === 0 ||
    !additionalCostTypeModel.PaymentMode;

  const showPrinter = () => {
    setShowPrinterModal(true);
  };

  const getColumnTitles = () => {
    return [
      localize.name,
      localize.default_value,
      localize.generates_technician_payment,
      localize.payment_mode,
      localize.value,
      localize.notification,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          title={localize.print}
          report_title={localize.additional_cost_type_title}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={ADDITIONAL_COST_TYPE_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="additional_cost_type"
        />
      )}

      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showEditModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_additional_cost_type}
          onClickToSubmit={() => {
            handleUpdateRequest(additionalCostTypeModel);
            setShowEditModal(false);
            setAdditionalCostTypeModel(getClearAdditionalCostTypeModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setAdditionalCostTypeModel(getClearAdditionalCostTypeModel());
          }}
          Component={
            <AddOrEditAdditionalCostType
              model={additionalCostTypeModel}
              setModel={setAdditionalCostTypeModel}
            />
          }
          validators={disableSaveChanges()}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.additional_cost_type_title}
            subtitle={localize.additional_cost_type_description}
          />

          <SearcherAndAdd
            printButtonMethod={showPrinter}
            entityName="additional_cost_type_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_additional_cost_type}
                onClickToSubmit={() => {
                  handleCreateRequest(additionalCostTypeModel);
                  setShowAddModal(false);
                  setAdditionalCostTypeModel(getClearAdditionalCostTypeModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setAdditionalCostTypeModel(getClearAdditionalCostTypeModel());
                }}
                Component={
                  <AddOrEditAdditionalCostType
                    model={additionalCostTypeModel}
                    setModel={setAdditionalCostTypeModel}
                  />
                }
                validators={disableSaveChanges()}
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={additionalCostTypeModel.Name}
          entityType={localize.additional_cost_type}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setAdditionalCostTypeModel(getClearAdditionalCostTypeModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(additionalCostTypeModel.Id);
            setShowDeleteModal(false);
            setAdditionalCostTypeModel(getClearAdditionalCostTypeModel());
          }}
        />
      )}

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexAdditionalCostTypes}
            setPageIndex={setPageIndexAdditionalCostTypes}
            dataArray={additionalCostTypeData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          additionalCostTypeData.Data?.length !== 0
            ? additionalCostTypeDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setAdditionalCostTypeModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setAdditionalCostTypeModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Name,
                  <PaymentTypeValue
                    generatesPaymentToTechnician
                    paymentMode="FIXED"
                    paymentValue={item.DefaultValue}
                  />,
                  <TrueFalseImage value={item.GeneratesPaymentToTechnician} />,
                  <ValueOrNoDataLabel
                    data={
                      item.GeneratesPaymentToTechnician
                        ? getPaymentTypeModeLabel(item.PaymentMode)
                        : null
                    }
                  />,
                  <PaymentTypeValue
                    generatesPaymentToTechnician={
                      item.GeneratesPaymentToTechnician
                    }
                    paymentMode={item.PaymentMode}
                    paymentValue={item.PaymentValue}
                  />,
                  <TrueFalseImage value={item.createNotification} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(8)]
        }
      />
    </div>
  );
}
